import { IDigitalDataEvent, IDigitalDataEventData, IUserBaseEvent, ITrackEvent, IPageViewEventData } from '@nab/x-types';

export const EVENT_TYPES = {
    userAction: 'user-action',
    miniappLoaded: 'miniapp-loaded',
    pageView: 'page-view',
    userMoment: 'user-moment'
};

export class TrackEvent implements ITrackEvent {
    constructor(private emitEvent: <T extends IUserBaseEvent = IDigitalDataEvent>(payload: T) => Promise<boolean>) {}

    /**
     * Track your userAction event, eventType is already populated. Just pass in the data object to track
     * @param data your digitalData attrs for a userAction event
     * @example
     * ```typescript
     *  // YourComponent.tsx
     *  import { useUser } from '@nab/nab-x-react';
     *  ....
     *  const { trackEvent } = useUser();
     *
     *  trackEvent.userAction({
     *      name: 'nameValue',
     *      action: 'actionValue',
     *      someOtherKey: 'someOtherValue'
     *  });
     * ```
     */
    public userAction(data: IDigitalDataEventData): void {
        this.emitEvent({ eventType: EVENT_TYPES.userAction, data });
    }

    /**
     * Track your userMoment event, eventType is already populated. Just pass in the data object to track
     * @param data your digitalData attrs for a userMoment event
     * @example
     * ```typescript
     *  // YourComponent.tsx
     *  import { useUser } from '@nab/nab-x-react';
     *  ....
     *  const { trackEvent } = useUser();
     *
     *  trackEvent.userMoment({
     *      name: 'nameValue',
     *      action: 'actionValue',
     *      someOtherKey: 'someOtherValue'
     *  });
     * ```
     */
    public userMoment(data: IDigitalDataEventData): void {
        this.emitEvent({ eventType: EVENT_TYPES.userMoment, data });
    }

    /**
     * Track your pageView event, eventType is already populated. Just pass in the data object to track
     * The Channel attr to be set against the window.digitialData.components.channel is already preset
     * @param data your digitalData attrs for a pageView event
     * @example
     * ```typescript
     *  // YourComponent.tsx
     *  import { useUser } from '@nab/nab-x-react';
     *  ....
     *  const { trackEvent } = useUser();
     *
     *  trackEvent.pageView({
     *      name: 'nameValue',
     *      action: 'actionValue',
     *      componentName: 'componentNameValue',
     *      section: 'sectionValue',
     *      someOtherKey: 'someOtherValue'
     *  });
     * ```
     */
    public pageView(data: IPageViewEventData): void {
        this.emitEvent({
            eventType: EVENT_TYPES.pageView,
            data: { ...data, channel: (window as any).digitalData.components.channel }
        });
    }

    /**
     * Track your miniappLoaded event, eventType is already populated. Just pass in the data object to track
     * The Channel attr to be set against the window.digitialData.components.channel is already preset
     * @param data your digitalData attrs for a miniappLoaded event
     * @example
     * ```typescript
     *  // YourComponent.tsx
     *  import { useUser } from '@nab/nab-x-react';
     *  ....
     *  const { trackEvent } = useUser();
     *
     *  trackEvent.miniappLoaded({
     *      name: 'nameValue',
     *      action: 'actionValue',
     *      someOtherKey: 'someOtherValue'
     *  });
     * ```
     */
    public miniappLoaded(data: IDigitalDataEventData): void {
        this.emitEvent({
            eventType: EVENT_TYPES.miniappLoaded,
            data: { ...data, channel: (window as any).digitalData.components.channel }
        });
    }
}
